(() => {
  if (customElements.get('mamule-home')) return;
  const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
  class MamuleHome extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      this._cleanup = [];
      this._reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this._abort.signal;
      this.querySelectorAll('[data-mm-close]').forEach(button => button.addEventListener('click', () => button.closest('dialog')?.close(), { signal }));
      this.querySelectorAll('dialog').forEach(dialog => dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } }, { signal }));
      this.querySelector('[data-mm-open-menu]')?.addEventListener('click', () => this.querySelector('[data-mm-menu]')?.showModal(), { signal });
      this.querySelector('[data-mm-open-art]')?.addEventListener('click', () => this.querySelector('[data-mm-art]')?.showModal(), { signal });
      this.querySelectorAll('[data-mm-view]').forEach(button => button.addEventListener('click', () => {
        const selected = button.dataset.mmView;
        this.querySelectorAll('[data-mm-view]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        this.querySelectorAll('[data-mm-detail]').forEach(image => { image.hidden = image.dataset.mmDetail !== selected; });
        const label = this.querySelector('[data-mm-detail-number]');
        if (label) label.textContent = String(Number(selected) + 1).padStart(2, '0');
      }, { signal }));
      const enabled = this.dataset.mmMotionEnabled !== 'false';
      if (enabled && !this._reduced.matches && 'IntersectionObserver' in window) {
        this.classList.add('mm-animated');
        const reveal = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.remove('mm-await'); reveal.unobserve(entry.target); } }), { threshold: .08 });
        this.querySelectorAll('[data-mm-reveal]').forEach(el => { el.classList.add('mm-await'); reveal.observe(el); });
        this._cleanup.push(() => reveal.disconnect());
      }
      this._cleanup.push(() => this.classList.remove('mm-animated'));
      this._reduced.addEventListener('change', () => {
        if (this._reduced.matches) this.querySelectorAll('.mm-await').forEach(el => el.classList.remove('mm-await'));
      }, { signal });
      this.initLeather(enabled);
      this.initMacro(enabled);
    }
    disconnectedCallback() {
      this._abort?.abort();
      this._cleanup?.forEach(fn => fn());
      this._abort = null;
    }
    initMacro(enabled) {
      const section = this.querySelector('[data-mm-macro]');
      const image = section?.querySelector('.mm-macro-photo');
      if (!section || !image || !enabled) return;
      const signal = this._abort.signal;
      let pending = 0;
      const update = () => {
        pending = 0;
        if (this._reduced.matches) { image.style.transform = ''; return; }
        const r = section.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        const offset = clamp((r.top + r.height / 2 - innerHeight / 2) / innerHeight, -1, 1) * 28;
        image.style.transform = `scale(1.09) translateY(${offset}px)`;
      };
      const schedule = () => { if (!pending) pending = requestAnimationFrame(update); };
      addEventListener('scroll', schedule, { passive: true, signal });
      addEventListener('resize', schedule, { passive: true, signal });
      this._reduced.addEventListener('change', schedule, { signal });
      schedule();
      this._cleanup.push(() => cancelAnimationFrame(pending));
    }
    initLeather(enabled) {
      const stage = this.querySelector('[data-mm-leather]');
      const canvas = stage?.querySelector('[data-mm-canvas]');
      const source = stage?.querySelector('[data-mm-hide]');
      const section = this.querySelector('[data-mm-material]');
      const toggle = this.querySelector('[data-mm-motion]');
      if (!stage || !canvas || !source || !section) return;
      if (!enabled) { if (toggle) toggle.hidden = true; return; }
      let gl;
      try { gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: false, powerPreference: 'low-power' }); } catch { gl = null; }
      if (!gl) { this.initLeatherFallback(stage, source, section, toggle); return; }
      const signal = this._abort.signal;
      let program, buffer, texture, resizeObserver, visibilityObserver;
      let frame = 0, alive = true, ready = false, visible = false, paused = false;
      let elapsed = 0, previousTime = 0, progress = 0, velocity = 0, lastScroll = scrollY;
      const pointer = [0, 0], targetPointer = [0, 0];
      const vertexSource = `
        attribute vec2 a_position;
        uniform float u_time;
        uniform float u_progress;
        uniform float u_velocity;
        uniform vec2 u_pointer;
        uniform vec2 u_fit;
        varying vec2 v_uv;
        varying float v_light;
        void main() {
          v_uv = (a_position + 1.0) * 0.5;
          float x = a_position.x;
          float y = a_position.y;
          float phase = u_time * 0.58 + u_progress * 4.8;
          float amplitude = 0.072 + abs(u_velocity) * 0.05;
          float z = sin(x * 3.8 + phase) * amplitude + cos(y * 3.2 - phase * 0.55) * 0.04;
          x += sin(y * 4.3 + phase) * (0.018 + abs(u_velocity) * 0.009);
          y += sin(x * 3.0 - phase) * 0.015;
          float turn = -0.10 + u_progress * 0.19 + u_pointer.x * 0.055;
          vec2 pos = vec2(x * cos(turn) - y * sin(turn), x * sin(turn) + y * cos(turn));
          pos.x += z * (0.2 + u_pointer.x * 0.25);
          pos.y += z * (0.4 + u_pointer.y * 0.2);
          pos += u_pointer * 0.018;
          float scale = 1.0 + u_progress * 0.075;
          gl_Position = vec4(pos * u_fit * scale / (1.0 + z * 0.16), z * 0.1, 1.0);
          float dx = cos(a_position.x * 3.8 + phase) * amplitude * 3.8;
          float dy = -sin(a_position.y * 3.2 - phase * 0.55) * 0.128;
          vec3 normal = normalize(vec3(-dx, -dy, 1.0));
          v_light = 0.72 + 0.30 * dot(normal, normalize(vec3(-0.35, 0.5, 1.0)));
        }
      `;
      const fragmentSource = `
        precision mediump float;
        uniform sampler2D u_texture;
        varying vec2 v_uv;
        varying float v_light;
        void main() {
          vec4 color = texture2D(u_texture, v_uv);
          if (color.a < 0.004) discard;
          gl_FragColor = vec4(color.rgb * v_light, color.a);
        }
      `;
      const shader = (type, code) => {
        const item = gl.createShader(type);
        gl.shaderSource(item, code); gl.compileShader(item);
        if (!gl.getShaderParameter(item, gl.COMPILE_STATUS)) { gl.deleteShader(item); throw new Error('Shader unavailable'); }
        return item;
      };
      try {
        const vs = shader(gl.VERTEX_SHADER, vertexSource), fs = shader(gl.FRAGMENT_SHADER, fragmentSource);
        program = gl.createProgram(); gl.attachShader(program, vs); gl.attachShader(program, fs); gl.linkProgram(program);
        gl.deleteShader(vs); gl.deleteShader(fs);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Program unavailable');
        gl.useProgram(program);
        const mesh = [], columns = 64, rows = 40;
        for (let y = 0; y < rows; y++) for (let x = 0; x < columns; x++) {
          const x0 = x / columns * 2 - 1, x1 = (x + 1) / columns * 2 - 1;
          const y0 = y / rows * 2 - 1, y1 = (y + 1) / rows * 2 - 1;
          mesh.push(x0, y0, x1, y0, x0, y1, x0, y1, x1, y0, x1, y1);
        }
        buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mesh), gl.STATIC_DRAW);
        const position = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
        const uniforms = Object.fromEntries(['u_time', 'u_progress', 'u_velocity', 'u_pointer', 'u_fit', 'u_texture'].map(name => [name, gl.getUniformLocation(program, name)]));
        const shouldAnimate = () => ready && alive && visible && !paused && !this._reduced.matches && !document.hidden;
        const resize = () => {
          if (!alive) return;
          const ratio = Math.min(devicePixelRatio || 1, 1.5);
          const width = Math.max(1, Math.round(stage.clientWidth * ratio));
          const height = Math.max(1, Math.round(stage.clientHeight * ratio));
          if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
          gl.viewport(0, 0, width, height);
        };
        const draw = () => {
          if (!ready || !alive) return;
          resize();
          const r = section.getBoundingClientRect();
          const targetProgress = clamp(-r.top / Math.max(1, r.height - innerHeight), 0, 1);
          if (!paused && !this._reduced.matches) {
            progress += (targetProgress - progress) * 0.075;
            velocity += (clamp((scrollY - lastScroll) / 80, -1, 1) - velocity) * .13;
            lastScroll = scrollY;
            pointer[0] += (targetPointer[0] - pointer[0]) * .035;
            pointer[1] += (targetPointer[1] - pointer[1]) * .035;
          }
          const aspect = canvas.width / canvas.height;
          const imageAspect = source.naturalWidth / source.naturalHeight;
          gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
          gl.uniform1f(uniforms.u_time, elapsed);
          gl.uniform1f(uniforms.u_progress, progress);
          gl.uniform1f(uniforms.u_velocity, velocity);
          gl.uniform2f(uniforms.u_pointer, pointer[0], pointer[1]);
          gl.uniform2f(uniforms.u_fit, Math.min(1, imageAspect / aspect) * .87, Math.min(1, aspect / imageAspect) * .87);
          gl.drawArrays(gl.TRIANGLES, 0, mesh.length / 2);
        };
        const animate = time => {
          frame = 0;
          if (!shouldAnimate()) { previousTime = 0; return; }
          if (previousTime) elapsed += Math.min((time - previousTime) / 1000, .05);
          previousTime = time;
          draw();
          frame = requestAnimationFrame(animate);
        };
        const sync = () => {
          if (!alive) return;
          if (toggle) {
            toggle.hidden = this._reduced.matches;
            toggle.setAttribute('aria-pressed', String(paused));
            toggle.setAttribute('aria-label', paused ? 'Animer le cuir' : 'Mettre en pause le mouvement du cuir');
            const text = toggle.querySelector('span'); if (text) text.textContent = paused ? 'Animer' : 'Pause';
          }
          cancelAnimationFrame(frame); frame = 0; previousTime = 0; lastScroll = scrollY;
          if (shouldAnimate()) frame = requestAnimationFrame(animate);
          else draw();
        };
        const loadTexture = () => {
          if (ready || !alive || !source.naturalWidth) return;
          try {
            texture = gl.createTexture(); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
            gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.uniform1i(uniforms.u_texture, 0);
            gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            if (gl.getError() !== gl.NO_ERROR) throw new Error('Texture unavailable');
            ready = true; draw(); stage.classList.add('mm-gl-ready'); sync();
          } catch { alive = false; stage.classList.remove('mm-gl-ready'); this.initLeatherFallback(stage, source, section, toggle); }
        };
        source.addEventListener('load', loadTexture, { signal });
        if (source.complete) loadTexture();
        toggle?.addEventListener('click', () => { if (alive) { paused = !paused; sync(); } }, { signal });
        stage.addEventListener('pointermove', event => {
          if (event.pointerType === 'touch') return;
          const r = stage.getBoundingClientRect();
          targetPointer[0] = clamp((event.clientX - r.left) / r.width * 2 - 1, -1, 1);
          targetPointer[1] = clamp(-((event.clientY - r.top) / r.height * 2 - 1), -1, 1);
        }, { passive: true, signal });
        stage.addEventListener('pointerleave', () => { targetPointer[0] = 0; targetPointer[1] = 0; }, { signal });
        this._reduced.addEventListener('change', sync, { signal });
        document.addEventListener('visibilitychange', sync, { signal });
        canvas.addEventListener('webglcontextlost', () => { alive = false; cancelAnimationFrame(frame); stage.classList.remove('mm-gl-ready'); this.initLeatherFallback(stage, source, section, toggle); }, { signal });
        if ('IntersectionObserver' in window) {
          visibilityObserver = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: .01 });
          visibilityObserver.observe(stage);
        } else { visible = true; sync(); }
        if ('ResizeObserver' in window) { resizeObserver = new ResizeObserver(() => { if (ready && !shouldAnimate()) draw(); }); resizeObserver.observe(stage); }
        this._cleanup.push(() => { alive = false; cancelAnimationFrame(frame); visibilityObserver?.disconnect(); resizeObserver?.disconnect(); stage.classList.remove('mm-gl-ready'); if (texture) gl.deleteTexture(texture); gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.getExtension('WEBGL_lose_context')?.loseContext(); });
      } catch {
        alive = false; stage.classList.remove('mm-gl-ready'); this.initLeatherFallback(stage, source, section, toggle);
        if (texture) gl.deleteTexture(texture); if (buffer) gl.deleteBuffer(buffer); if (program) gl.deleteProgram(program);
      }
    }
    initLeatherFallback(stage, image, section, toggle) {
      if (stage.classList.contains('mm-css-ready') || !this.isConnected) return;
      stage.classList.add('mm-css-ready');
      const signal = this._abort.signal;
      let frame = 0, progress = 0, paused = false, pointerX = 0, pointerY = 0;
      const syncButton = () => {
        if (!toggle) return;
        toggle.hidden = this._reduced.matches;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Animer le cuir' : 'Mettre en pause le mouvement du cuir');
        toggle.querySelector('span').textContent = paused ? 'Animer' : 'Pause';
      };
      const update = () => {
        frame = 0;
        if (this._reduced.matches) { image.style.transform = ''; return; }
        if (paused || document.hidden) return;
        const r = section.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        const target = clamp(-r.top / Math.max(1, r.height - innerHeight), 0, 1);
        progress += (target - progress) * .08;
        image.style.transform = `perspective(1100px) translateY(${-7 + progress * 14}px) rotateX(${Math.sin(progress * 2.4) * 4 + pointerY}deg) rotateY(${-3 + progress * 6 + pointerX * 1.4}deg) rotateZ(${-2 + progress * 4}deg) skewX(${Math.sin(progress * 3) * .7}deg) scale(${.94 + progress * .025})`;
        if (Math.abs(target - progress) > .0008) frame = requestAnimationFrame(update);
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      addEventListener('scroll', schedule, { passive: true, signal });
      addEventListener('resize', schedule, { passive: true, signal });
      document.addEventListener('visibilitychange', schedule, { signal });
      this._reduced.addEventListener('change', () => { syncButton(); schedule(); }, { signal });
      toggle?.addEventListener('click', () => { paused = !paused; syncButton(); if (!paused) schedule(); }, { signal });
      stage.addEventListener('pointermove', event => {
        if (event.pointerType === 'touch') return;
        const r = stage.getBoundingClientRect();
        pointerX = clamp((event.clientX - r.left) / r.width * 2 - 1, -1, 1);
        pointerY = clamp((event.clientY - r.top) / r.height * 2 - 1, -1, 1);
        schedule();
      }, { passive: true, signal });
      stage.addEventListener('pointerleave', () => { pointerX = 0; pointerY = 0; schedule(); }, { signal });
      syncButton(); schedule();
      this._cleanup.push(() => { cancelAnimationFrame(frame); stage.classList.remove('mm-css-ready'); image.style.transform = ''; });
    }
  }
  customElements.define('mamule-home', MamuleHome);
})();
